import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Search, Plus, Edit, Trash2, Loader2, Boxes, Factory,
  AlertTriangle, Package, FileDown
} from 'lucide-react';
import { skeletonKeys } from '../lib/listKeys';

function CategoryTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = categories.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditItem(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        const res = await api.put(`/categories/${encodeURIComponent(editItem.name)}`, form);
        const updated = res.data.products_updated || 0;
        toast.success(editItem.name !== form.name
          ? `Renamed. ${updated} product(s) updated.`
          : 'Category updated');
      } else {
        await api.post('/categories', form);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (name) => {
    try {
      await api.delete(`/categories/${encodeURIComponent(name)}`);
      toast.success('Category deleted');
      setDeleteConfirm(null);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="category-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            data-testid="category-search"
          />
        </div>
        <Button onClick={openAdd} className="gap-1.5" data-testid="add-category-btn">
          <Plus className="h-4 w-4" /> Add Category
        </Button>
      </div>

      <Card data-testid="categories-table-card">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category Name</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                <TableHead className="text-right">Products</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                skeletonKeys(4, 'category-skeleton').map((key) => (
                  <TableRow key={key}><TableCell colSpan={4}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    <Boxes className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No categories found</p>
                  </TableCell>
                </TableRow>
              ) : filtered.map(cat => (
                <TableRow key={cat.name} data-testid={`cat-row-${cat.name}`}>
                  <TableCell className="font-medium">{cat.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-[300px] truncate">
                    {cat.description || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="text-xs">{cat.product_count}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.name}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirm(cat)}
                        data-testid={`delete-cat-${cat.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Pain Relief"
                data-testid="category-name-input"
              />
              {editItem && editItem.name !== form.name && form.name.trim() && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Renaming will update all {editItem.product_count} product(s) in this category.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                rows={3}
                data-testid="category-desc-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-category-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-category-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Delete Category
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm?.product_count > 0 ? (
            <p className="text-sm text-muted-foreground">
              Cannot delete <strong>{deleteConfirm.name}</strong> — it has {deleteConfirm.product_count} product(s).
              Rename or reassign products first.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            {deleteConfirm?.product_count === 0 && (
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.name)} data-testid="confirm-delete-category-btn">
                Delete
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManufacturerTab() {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', country: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/manufacturers');
      setManufacturers(res.data.manufacturers || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = manufacturers.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) ||
    (m.country || '').toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditItem(null);
    setForm({ name: '', description: '', country: '' });
    setDialogOpen(true);
  };

  const openEdit = (mfr) => {
    setEditItem(mfr);
    setForm({ name: mfr.name, description: mfr.description || '', country: mfr.country || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        const res = await api.put(`/manufacturers/${encodeURIComponent(editItem.name)}`, form);
        const updated = res.data.products_updated || 0;
        toast.success(editItem.name !== form.name
          ? `Renamed. ${updated} product(s) updated.`
          : 'Manufacturer updated');
      } else {
        await api.post('/manufacturers', form);
        toast.success('Manufacturer created');
      }
      setDialogOpen(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (name) => {
    try {
      await api.delete(`/manufacturers/${encodeURIComponent(name)}`);
      toast.success('Manufacturer deleted');
      setDeleteConfirm(null);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="manufacturer-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search manufacturers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            data-testid="manufacturer-search"
          />
        </div>
        <Button onClick={openAdd} className="gap-1.5" data-testid="add-manufacturer-btn">
          <Plus className="h-4 w-4" /> Add Manufacturer
        </Button>
      </div>

      <Card data-testid="manufacturers-table-card">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Manufacturer Name</TableHead>
                <TableHead className="hidden md:table-cell">Country</TableHead>
                <TableHead className="hidden lg:table-cell">Description</TableHead>
                <TableHead className="text-right">Products</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                skeletonKeys(4, 'manufacturer-skeleton').map((key) => (
                  <TableRow key={key}><TableCell colSpan={5}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                    <Factory className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No manufacturers found</p>
                  </TableCell>
                </TableRow>
              ) : filtered.map(mfr => (
                <TableRow key={mfr.name} data-testid={`mfr-row-${mfr.name}`}>
                  <TableCell className="font-medium">{mfr.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{mfr.country || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[250px] truncate">{mfr.description || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="text-xs">{mfr.product_count}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(mfr)} data-testid={`edit-mfr-${mfr.name}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirm(mfr)}
                        data-testid={`delete-mfr-${mfr.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="manufacturer-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Manufacturer' : 'New Manufacturer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Cipla"
                data-testid="manufacturer-name-input"
              />
              {editItem && editItem.name !== form.name && form.name.trim() && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Renaming will update all {editItem.product_count} product(s) by this manufacturer.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input
                value={form.country}
                onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
                placeholder="e.g. India"
                data-testid="manufacturer-country-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                rows={3}
                data-testid="manufacturer-desc-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-manufacturer-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-manufacturer-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-manufacturer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Delete Manufacturer
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm?.product_count > 0 ? (
            <p className="text-sm text-muted-foreground">
              Cannot delete <strong>{deleteConfirm.name}</strong> — it has {deleteConfirm.product_count} product(s).
              Rename or reassign products first.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            {deleteConfirm?.product_count === 0 && (
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.name)} data-testid="confirm-delete-manufacturer-btn">
                Delete
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatalogPdfExportDialog() {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('all');
  const [otcOnly, setOtcOnly] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/categories').then(res => setCategories(res.data.categories || [])).catch(() => {});
  }, [open]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (otcOnly) params.otc_only = true;
      const res = await api.get('/catalog/pdf', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `product-catalog-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Catalog PDF downloaded');
      setOpen(false);
    } catch {
      toast.error('Failed to generate catalog PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-1.5" onClick={() => setOpen(true)} data-testid="export-pdf-catalog-btn">
        <FileDown className="h-4 w-4" /> Export PDF Catalog
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="export-pdf-catalog-dialog">
          <DialogHeader>
            <DialogTitle>Export PDF Catalog</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="export-pdf-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="otc-only-checkbox" checked={otcOnly} onCheckedChange={v => setOtcOnly(!!v)} data-testid="export-pdf-otc-only-checkbox" />
              <Label htmlFor="otc-only-checkbox" className="cursor-pointer">OTC only</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="export-pdf-cancel-btn">Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} data-testid="export-pdf-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
              Generate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CatalogPage() {
  return (
    <div className="space-y-5" data-testid="catalog-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Catalog Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage product categories and manufacturers
          </p>
        </div>
        <CatalogPdfExportDialog />
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList data-testid="catalog-tabs">
          <TabsTrigger value="categories" className="gap-1.5" data-testid="tab-categories">
            <Boxes className="h-3.5 w-3.5" /> Categories
          </TabsTrigger>
          <TabsTrigger value="manufacturers" className="gap-1.5" data-testid="tab-manufacturers">
            <Factory className="h-3.5 w-3.5" /> Manufacturers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="categories">
          <CategoryTab />
        </TabsContent>
        <TabsContent value="manufacturers">
          <ManufacturerTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
